import {
  Box,
  Checkbox,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {BaseDialog, BaseList, FontIconChevron} from '@resurs-internal/mui';
import {useCountry} from '@resurs-internal/rb-app-api';
import {FontIcon, IconBadge} from 'components';
import {TextBulletList} from 'components/TextBulletList/TextBulletList';
import {useOpenDocument, useTranslate} from 'hooks';
import React, {useState} from 'react';

type PPIDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  submitText: string;
  subtitle: string;
  documents: {
    title: string;
    link?: string;
  }[];
};

export const PPIDialog: React.FC<PPIDialogProps> = ({
  open,
  onClose,
  onSubmit,
  subtitle,
  submitText,
  documents,
}) => {
  const openDoc = useOpenDocument();
  const theme = useTheme();
  const translate = useTranslate();
  const [checked, setChecked] = useState(false);
  const country = useCountry();
  const countryLower = country.code.toLowerCase();

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      image={'/images/ppi.jpg'}
      maxWidth={'sm'}
      actions={[
        {
          title: translate('common.cancel'),
          onClick: onClose,
        },
        {
          title: submitText,
          disabled: !checked,
          color: 'primary',
          onClick: onSubmit,
        },
      ]}>
      <DialogTitle variant={'h3'} textAlign={'center'}>
        {translate(`${countryLower}.ppi.title`)}
      </DialogTitle>
      <DialogContent>
        <Stack>
          <Typography
            variant={'body1'}
            mb={4}
            whiteSpace={'break-spaces'}
            color={theme.palette.text.secondary}>
            {subtitle}
          </Typography>
          <Typography variant={'subtitle1'} mb={1} component={'h3'}>
            {translate(`${countryLower}.ppi.requirements.title`)}
          </Typography>
          <TextBulletList
            items={translate(`${countryLower}.ppi.requirements.bullets`).split(
              '\n',
            )}
          />
          <Typography mb={4} variant={'body1'} whiteSpace={'break-spaces'}>
            {translate(`${countryLower}.ppi.requirements.more`)}
          </Typography>
        </Stack>
        <Box
          sx={(theme) => ({
            p: 3,
            background: theme.palette.grey['100'],
          })}>
          <Stack spacing={2}>
            <Stack direction={'row'} alignItems={'center'} spacing={2}>
              <IconBadge
                type={'font'}
                icon={'scale-balanced'}
                inactive={true}
              />
              <Typography typography={'subtitle2'}>
                {translate(`${countryLower}.ppi.documents.title`)}
              </Typography>
            </Stack>
            <BaseList
              component={'ul'}
              items={documents?.map((document, i) => {
                return {
                  id: `document-${i}`,
                  testID: `document-${i}`,
                  icon: <FontIcon icon={'file-lines'} />,
                  title: document.title,
                  endAdornment: <FontIconChevron direction={'right'} />,
                  onClick: () => {
                    openDoc.url(document.link, '_blank');
                  },
                };
              })}
            />
            <Stack direction={'row'} alignItems={'center'} spacing={2}>
              <Checkbox
                slotProps={{
                  input: {
                    'aria-label': `${translate(
                      `${countryLower}.ppi.accept.bullets`,
                    )}`,
                  },
                }}
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
              />
              <Stack direction={'column'} spacing={1}>
                <Typography whiteSpace={'break-spaces'}>
                  {translate(`${countryLower}.ppi.accept.title`)}
                </Typography>
                <TextBulletList
                  items={translate(`${countryLower}.ppi.accept.bullets`).split(
                    '\n',
                  )}
                />
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
    </BaseDialog>
  );
};
